import React, { Suspense } from 'react';
import { Route, Routes, useParams } from 'react-router';
import ProfilePage from '../components/ProfileRoute/ProfilePage';
import Activity from '../components/Activity/Activity';
import GroupsPage from '../components/GroupsRoute/GroupsPage';
import NotificationsPage from '../components/Notifications/NotificationsPage';
import TaskManagement from '../components/TaskManagement/TaskManagement';
import TaskManagementDashboard from '../components/TaskManagement/TaskManagementDashboard';
import TaskManagementTimeline from '../components/TaskManagement/TaskManagementTimeline';
import TaskManagementMap from '../components/TaskManagement/TaskManagementMap';
import FeatureFlags from '../components/FeatureFlags/FeatureFlags';
import LogoutRoute from '../components/LogoutRoute/LogoutRoute';
import MapPage from '../components/MapPage/MapPage';
import { SubscriptionManagement } from '../components/SubscriptionManagement/SubscriptionManagement';
import { ToastContainer } from 'react-toastify';
import PratusLayout from './PratusLayout';
import RouteError from '../components/Error/RouteError';
import AIUsages from '../components/DisasterGPT/AIUsages';
import IAPWrapper from '../components/IAP/IAPWrapper';
import IAPLayout from '../components/IAP/IAPLayout';
import IAPDashboard from '../components/IAP/IAPDashboard';
import KnowledgeBase from '../components/KnowledgeBase/KnowledgeBase';
import IAPSettings from '../components/IAP/IAPSettings';
import IAPCalendar from '../components/IAP/IAPCalendar';
import ActionsPage from '../components/IAP/ActionsPage';
import ActionView from '../components/IAP/ActionView';
import IAPBulkResources from '../components/IAP/IAPBulkResources';
import ICSFormsPage from '../components/IAP/ICSFormsPage';
import FilesPage from '../components/IAP/FilesPage';
import OrgFilesPage from '../components/FileManagement/OrgFilesPage';
import IncidentConfigurationRedirect from '../components/IncidentConfig/IncidentConfigurationRedirect';
import ActivationConfirmation from '../components/IncidentConfig/ActivationConfirmation';
import NotificationJobs from '../components/Notifications/NotificationJobs';
import NotificationLogScreen from '../components/Notifications/NotificationLogScreen';
import NotificationComposeScreen from '../components/Notifications/NotificationComposeScreen';
import { LogoutResultPage } from 'components/Auth/LogoutResultPage';
import TeamsTabConfig from 'components/TeamsIntegration/TeamsTabConfig';
import Sitreps from 'components/SitrepsRoute/Sitreps';
import SingleSitrep from 'components/SitrepsRoute/SingleSitrep';
import Loading from 'components/Loading/Loading';
import AlertConfigurationsRoute from '../components/DisasterGPTAlerts/AlertConfigurationsRoute';
import AlertConfigSummaryPage from '../components/DisasterGPTAlerts/AlertConfigSummaryPage.js';
import AlertConfigWizardPage from '../components/DisasterGPTAlerts/AlertConfigWizardPage';
import AlertInstanceTable from '../components/DisasterGPTAlerts/AlertInstanceTable.js';
import AlertRunTable from '../components/DisasterGPTAlerts/AlertRunTable.js';
import AlertRecipientsTable from '../components/DisasterGPTAlerts/AlertRecipientsTable';
import NotFound from './NotFound';
import { useSubscriptionLevel } from '../components/SubscriptionManagement/useSubscriptionLevel';
import InitialResponsePage from '../components/IAP/initialResponsePage/InitialResponsePage';
import Notifications from 'components/MapPage/CorporateViewTabs/Notifications';
import ReportsView from 'components/Reports/ReportsView';
import IAPRoster from 'components/IAP/IAPRoster';
import { UscgMapContextProvider } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { toastConfig } from '../assets/data/config';
import { ResourcesFullPage } from 'components/MapPage/CorporateViewTabs/ResourceTab/ResourcesFullPage';
import IAPResources from 'components/IAP/IAPResources';
import IncidentProfile from 'components/IAP/IncidentProfile/IncidentProfile';
import ExternalUserNoIncidents from './ExternalUser';

const ExerciseRoutes = React.lazy(() => import('./ExerciseRoutes'));
const UtilityImpactPredictionWrapper = React.lazy(
  () =>
    import('components/UtilityImpactPrediction/UtilityImpactPredictionWrapper')
);

const PratusRoutes: React.FC<{ isDev: boolean }> = ({ isDev }) => {
  const { utilityImpactLevel } = useSubscriptionLevel();

  return (
    <UscgMapContextProvider>
      <Routes>
        <Route path="/logout" element={<LogoutResultPage />} />
        <Route path="/teamsconfig" element={<TeamsTabConfig />} />
        <Route element={<PratusLayout />} errorElement={<RouteError />}>
          <Route path="/sitreps">
            <Route index element={<Sitreps />} />
            <Route path=":sitrepId" element={<SingleSitrep />} />
          </Route>
          <Route path="/knowledge_base" element={<KnowledgeBase />} />
          <Route path="/org_files" element={<OrgFilesPage />} />
          <Route path="/profile/*" element={<ProfilePage />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/groups/*" element={<GroupsPage />} />
          <Route path="/notifications" element={<NotificationsPage />}>
            <Route index element={<NotificationComposeScreen />} />
            <Route path="compose" element={<NotificationComposeScreen />} />
            <Route path="jobs" element={<NotificationJobs />} />
            <Route path="logs" element={<NotificationLogScreen />} />
          </Route>
          <Route
            path="/incident_configuration"
            element={<IncidentConfigurationRedirect />}
          />
          <Route path="/task_management" element={<TaskManagement />}>
            <Route index element={<TaskManagementDashboard />} />
            <Route path="timeline" element={<TaskManagementTimeline />} />
            <Route path="map" element={<TaskManagementMap />} />
          </Route>
          {isDev && <Route path="/feature_flags" element={<FeatureFlags />} />}
          {isDev && <Route path="/ai_usages" element={<AIUsages />} />}
          {utilityImpactLevel !== 'None' ? (
            <Route
              path="/utility_impact_prediction"
              element={
                <Suspense fallback={<Loading />}>
                  <UtilityImpactPredictionWrapper />
                </Suspense>
              }
            />
          ) : (
            <Route path="/utility_impact_prediction" element={<NotFound />} />
          )}
          <Route
            path="/external_user_no_incidents"
            element={<ExternalUserNoIncidents />}
          />
          <Route path="/aialerts" element={<AlertConfigurationsRoute />} />
          <Route
            path="/aialerts/create"
            element={<AlertConfigWizardPage mode="create" />}
          />
          <Route
            path="/aialerts/:alert_config_id"
            element={
              <AlertConfigSummaryPage
                alertConfigId={useParams().alert_config_id || ''}
              />
            }
          />
          <Route
            path="/aialerts/:alert_config_id/edit"
            element={<AlertConfigWizardPage mode="edit" />}
          />
          <Route
            path="/aialerts/:alert_config_id/reports"
            element={<AlertInstanceTable />}
          />
          <Route
            path="/aialerts/:alert_config_id/reports/:report_id/recipients"
            element={<AlertRecipientsTable />}
          />
          <Route
            path="/aialerts/:alert_config_id/logs"
            element={<AlertRunTable />}
          />
          <Route path="/iap/:incidentId/*" element={<IAPWrapper />}>
            <Route element={<IAPLayout />}>
              <Route index element={<IAPDashboard />} />
              <Route path="map" element={<MapPage />} />
              <Route path="dashboard" element={<IAPDashboard />} />
              <Route path="settings" element={<IAPSettings />} />
              <Route path="legacy_resources/*" element={<IAPResources />} />
              <Route path="resources/*" element={<ResourcesFullPage />} />
              <Route path="actions" element={<ActionsPage />} />
              <Route path="actions/:actionId" element={<ActionView />} />
              <Route path="bulk_resources" element={<IAPBulkResources />} />
              <Route path="logs" element={<NotificationLogScreen />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="roster" element={<IAPRoster />} />
              <Route path="reports" element={<ReportsView />} />
              <Route path="calendar" element={<IAPCalendar />} />
              <Route path="forms" element={<ICSFormsPage />} />
              <Route path="profile" element={<IncidentProfile />} />
              <Route path="files" element={<FilesPage />} />
              <Route path="confirm" element={<ActivationConfirmation />} />
              <Route
                path="initial-response" // TODO: probably remove this?
                element={<InitialResponsePage />}
              />
            </Route>
          </Route>
          <Route path="/logout" element={<LogoutRoute />} />
          <Route path="/hub" element={<MapPage />} />
          <Route path="/" element={<MapPage />} />
          <Route path="/prepare/*" element={<ExerciseRoutes />} />
          <Route
            path="/subscription_management"
            element={<SubscriptionManagement />}
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      <ToastContainer {...toastConfig} />
    </UscgMapContextProvider>
  );
};

export default PratusRoutes;
